import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn';
import { createStatusColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/StatusColumn';
import { createCohortsColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/CohortsColumn';
import {
	AsyncFilterMultiSelectProps,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { FooterUniqueStat } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/FooterUniqueStat';
import { CollapsedListCount } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/CollapsedListCount';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { InvestorActionCell } from './InvestorActionCell';
import { InvestorActionCallbacks } from './types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import {
	CustomDetailDataTypeEnum,
	GetParsedInvestorRes,
	InvestorsListStatsRes,
	PivPersonRes,
} from '@dealstack/shared/lib/generated';
import { InvestorColumnSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/components/InvestorColumnSummary';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { Anchor, Flex, Text, Tooltip } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';
import { BooleanToggleFilter } from '@dealstack/frontend/shared/components/Table/MantineTable/Filters/BooleanToggleFilter';
import {
	ACCOUNT_STATUS_COLORS,
	PROGRAMME_STATUS_COLORS,
	getAccountStatusDefinition,
	getAccountStatusFilterOptions,
	getAccountStatusLabel,
	getProgrammeStatusDefinition,
	getProgrammeStatusFilterOptions,
	getProgrammeStatusLabel,
} from './investorsTableFilterOptions';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { getBooleanLabel } from '@dealstack/frontend/shared/other/react/booleanLabel';
import { ActionIconButton } from '@dealstack/frontend/shared/components/ActionIconButton/ActionIconButton.tsx';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	DATE_FORMATS,
	formatStringDateToRelative,
	formatStringDateToUI,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { PivSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorsTable/PivSummary.tsx';
import {
	COUNTRIES_NAME_CODE_OPTIONS,
	getCountryNameByCode,
} from '@dealstack/shared/lib/countries/countriesData';
import React from 'react';
import ReactCountryFlag from 'react-country-flag';

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>;
	stats?: InvestorsListStatsRes;
	actionCallbacks: InvestorActionCallbacks;
	isLocked?: boolean;
	disableSendInvestorInvitation?: boolean;
	currency?: string;
	investorFilterOptions: { label: string; value: string }[];
	investorFilterAsync: AsyncFilterMultiSelectProps;
	pivFilterOptions: { label: string; value: string }[];
	pivFilterAsync: AsyncFilterMultiSelectProps;
	mipClassifierFilterOptions: { label: string; value: string }[];
	hasEditorRole?: boolean;
	hasExportRole?: boolean;
	hasKycAdminRole?: boolean;
	dealId?: string;
	mipId?: string;
	enabledCustomDetails: Array<{ id: string; name: string; dataType: CustomDetailDataTypeEnum }>;
	getInvestmentVehicleLink?: (personId: string) => string;
}

export const getInvestorsTableColumns = ({
	tMipManage,
	tCommonUi,
	stats,
	actionCallbacks,
	isLocked,
	disableSendInvestorInvitation,
	currency,
	investorFilterOptions,
	investorFilterAsync,
	pivFilterOptions,
	pivFilterAsync,
	mipClassifierFilterOptions,
	hasEditorRole,
	hasExportRole,
	hasKycAdminRole,
	dealId,
	mipId,
	enabledCustomDetails,
	getInvestmentVehicleLink,
}: Props): ExtendedColumnDef<GetParsedInvestorRes>[] => [
	createTextColumn({
		id: 'investor',
		headerText: tMipManage('investors.table.investor'),
		columnLabel: tMipManage('investors.table.investor'),
		size: 240,
		minSize: 220,
		showFilter: true,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: investorFilterOptions, ...investorFilterAsync },
		filterText: tMipManage('investors.table.investor'),
		enableSorting: true,
		accessorFn: (row) => `${row.person?.firstName ?? ''} ${row.person?.lastName ?? ''}`,
		cell: ({ row }) => {
			const { person } = row.original;
			return (
				<InvestorColumnSummary
					displayName={person.displayName}
					email={person.email}
					emailDataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_INVESTOR_EMAIL}
				/>
			);
		},
		showFooter: true,
		footer: () => <FooterUniqueStat label="Unique count" value={stats?.investorCount ?? 0} />,
	}),

	createStatusColumn({
		id: 'accountStatus',
		headerText: tMipManage('investors.table.header.account_status'),
		columnLabel: tMipManage('investors.table.header.account_status'),
		enableSorting: true,
		showFilter: true,
		size: 200,
		minSize: 200,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: getAccountStatusFilterOptions(tMipManage) },
		accessorFn: (row) => row.accountStatus,
		cell: ({ row }) => {
			const { accountStatus, lastLoginAt } = row.original;
			const colors = ACCOUNT_STATUS_COLORS[accountStatus];
			const lastLoginItem = lastLoginAt && (
				<Tooltip label={formatStringDateToUI(lastLoginAt, DATE_FORMATS.CALENDAR_DATE)}>
					<Text fz="sm" c="dark.0">
						{tMipManage('investors.table.last_login_prefix', {
							lastLogin: formatStringDateToRelative(lastLoginAt),
						})}
					</Text>
				</Tooltip>
			);

			return (
				<Flex direction="column" gap="0.25rem" align="flex-start">
					<StatusBadge
						text={getAccountStatusLabel(accountStatus, tMipManage)}
						color={colors.color}
						textColor={colors.textColor}
						data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_INVESTMENTS_STATUS}
					/>

					{lastLoginItem}
				</Flex>
			);
		},
		showFooter: true,
		footerValue: stats?.accountStatusCounts,
		statusDefinition: getAccountStatusDefinition(tMipManage),
	}),

	createStatusColumn({
		id: 'programmeStatus',
		headerText: tMipManage('investors.table.header.programme_status'),
		columnLabel: tMipManage('investors.table.header.programme_status'),
		enableSorting: false,
		showFilter: true,
		size: 200,
		minSize: 200,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: getProgrammeStatusFilterOptions(tMipManage) },
		accessorFn: (row) => row.programmeStatus,
		cell: ({ row }) => {
			const status = row.original.programmeStatus;
			if (!status) {
				return null;
			}
			const colors = PROGRAMME_STATUS_COLORS[status] ?? {
				color: 'gray.4',
				textColor: 'dark',
			};
			return (
				<StatusBadge
					text={getProgrammeStatusLabel(status, tMipManage)}
					color={colors.color}
					textColor={colors.textColor}
				/>
			);
		},
		showFooter: true,
		footerValue: stats?.programmeStatusCounts,
		statusDefinition: getProgrammeStatusDefinition(tMipManage),
	}),
	createCohortsColumn<GetParsedInvestorRes>({
		id: 'mipClassifiers',
		headerText: tMipManage('investors.table.header.cohorts'),
		columnLabel: tMipManage('investors.table.header.cohorts'),
		filterOptions: mipClassifierFilterOptions,
		filterText: tMipManage('investors.table.header.cohorts'),
		getCohorts: (row) => row.mipClassifiers ?? [],
	}),
	createTextColumn({
		id: 'investmentVehicles',
		headerText: tMipManage('investors.table.header.investment_vehicles'),
		columnLabel: tMipManage('investors.table.header.investment_vehicles'),
		enableSorting: true,
		showFilter: true,
		filterVariant: 'multi-select',
		size: 200,
		minSize: 200,
		mantineFilterMultiSelectProps: { data: pivFilterOptions, ...pivFilterAsync },
		filterText: tMipManage('investors.table.header.investment_vehicles'),
		cell: ({ row }) => {
			const pivs = row.original.pivPersons ?? [];
			if (pivs.length === 0) {
				return LONG_DASH;
			}
			const [firstPiv, ...rest] = pivs;

			const renderPiv = (piv: PivPersonRes) => {
				const summary = (
					<PivSummary
						displayName={piv.displayName}
						countryCode={piv.jurisdictionCountryCode}
						regNumber={piv.registeredNumber}
					/>
				);

				const link = getInvestmentVehicleLink?.(piv.id);

				if (!link) {
					return summary;
				}

				return (
					<Anchor
						component={Link}
						to={link}
						underline="never"
						onClick={(event) => event.stopPropagation()}
						data-testid={makeComplexDataTestId(
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_INVESTMENT_VEHICLE_LINK,
							piv.id
						)}
					>
						{summary}
					</Anchor>
				);
			};

			return (
				<CollapsedListCount
					count={pivs.length - 1}
					allowTooltipSummary={true}
					tooltipLabel={
						<Flex direction="column" gap="0.5rem" p="0.25rem">
							{rest.map((item) => (
								<div key={item.id}>{renderPiv(item)}</div>
							))}
						</Flex>
					}
				>
					{renderPiv(firstPiv)}
				</CollapsedListCount>
			);
		},
		showFooter: true,
		footer: () => (
			<FooterUniqueStat label="Unique count" value={stats?.investmentVehicleCount ?? 0} />
		),
	}),

	{
		id: 'workInProgress',
		header: () => <Text fw={600}>{tMipManage('investors.table.header.work_in_progress')}</Text>,
		columnLabel: tMipManage('investors.table.header.work_in_progress'),
		enableSorting: false,
		showFilter: true,
		filterValueType: 'boolean',
		size: 200,
		minSize: 200,
		filter: ({ column }) => <BooleanToggleFilter column={column} />,
		cell: ({ row }) => {
			const { hasActiveInvestmentWorkflow, activeInvestmentWorkflowId } = row.original;
			const label = getBooleanLabel(hasActiveInvestmentWorkflow, tCommonUi);

			if (!hasActiveInvestmentWorkflow) {
				return label;
			}
			const investmentsPath = mipManagePath(MIP_MANAGE_ROUTES.INVESTMENTS, {
				dealId: dealId ?? '',
				mipId: mipId ?? '',
			});
			const filterParam = JSON.stringify([
				{ id: 'investmentWorkflowId', value: activeInvestmentWorkflowId },
			]);
			return (
				<Flex align="center" gap="0.25rem">
					<Text>{label}</Text>
					<Link
						to={{
							pathname: investmentsPath,
							search: `filters=${encodeURIComponent(filterParam)}`,
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<ActionIconButton customIcon={<IconExternalLink size={18} />} />
					</Link>
				</Flex>
			);
		},
	},

	createNumberColumn({
		id: 'programmeHoldings',
		headerText: tMipManage('investors.table.header.total_investment'),
		columnLabel: tMipManage('investors.table.header.total_investment'),
		enableSorting: true,
		lockPrecision: true,
		minPrecision: 2,
		maxPrecision: 2,
		size: 240,
		minSize: 220,
		accessorFn: (row) => {
			if (row.totalHoldings == null) {
				return null;
			}
			return { value: row.totalHoldings, currency: row.currency };
		},
		showFooter: true,
		footer: () => {
			const value = formatNumber({
				value: stats?.totalHoldingsSum ?? 0,
				currency,
				lockPrecision: true,
				minPrecision: 2,
				maxPrecision: 2,
				returnNumericalZero: true,
			});
			return <FooterUniqueStat value={value} align="flex-end" />;
		},
	}),
	createTextColumn({
		id: 'taxResidenceTin',
		headerText: tMipManage('investors.table.header.tax_residence_tin'),
		columnLabel: tMipManage('investors.table.header.tax_residence_tin'),
		size: 220,
		minSize: 220,
		enableSorting: false,
		showFilter: true,
		filterVariant: 'text',
		filterText: tMipManage('investors.table.header.tax_residence_tin'),
		cell: ({ row }) => {
			const taxResidences = row.original.taxResidences ?? [];
			if (taxResidences.length === 0) {
				return LONG_DASH;
			}
			const renderResidence = (tax: { countryCode: string; number: string | null }) => {
				const country = getCountryNameByCode(tax.countryCode) ?? tax.countryCode;
				return (
					<Flex direction="column" gap="0.125rem" align="flex-start">
						<Text c="dark.9" fz="md">
							{country}
						</Text>
						{tax.number && (
							<Text fz="sm" c="dark.0">
								{tMipManage('investors.table.tin_prefix', { tin: tax.number })}
							</Text>
						)}
					</Flex>
				);
			};
			const [first, ...rest] = taxResidences;
			return (
				<CollapsedListCount
					count={rest.length}
					allowTooltipSummary={true}
					tooltipLabel={
						<Flex direction="column" gap="0.5rem" p="0.25rem">
							{rest.map((tax, index) => (
								<React.Fragment
									key={`${tax.countryCode}-${tax.number ?? ''}-${index}`}
								>
									{renderResidence(tax)}
								</React.Fragment>
							))}
						</Flex>
					}
				>
					{renderResidence(first)}
				</CollapsedListCount>
			);
		},
	}),
	createTextColumn({
		id: 'countryOfBirth',
		headerText: tMipManage('investors.table.header.country_of_birth'),
		columnLabel: tMipManage('investors.table.header.country_of_birth'),
		size: 220,
		minSize: 220,
		enableSorting: false,
		showFilter: true,
		filterVariant: 'select',
		mantineFilterSelectProps: { data: COUNTRIES_NAME_CODE_OPTIONS },
		filterText: tMipManage('investors.table.header.country_of_birth'),
		cell: ({ row }) => getCountryNameByCode(row.original.countryOfBirth) || LONG_DASH,
	}),
	createTextColumn({
		id: 'citizenships',
		headerText: tMipManage('investors.table.header.citizenships'),
		columnLabel: tMipManage('investors.table.header.citizenships'),
		size: 220,
		minSize: 220,
		enableSorting: true,
		showFilter: true,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: COUNTRIES_NAME_CODE_OPTIONS },
		filterText: tMipManage('investors.table.header.citizenships'),
		cell: ({ row }) => {
			const citizenships = row.original.citizenships ?? [];
			if (citizenships.length === 0) {
				return LONG_DASH;
			}
			const renderCitizenship = (code: string) => (
				<Flex align="center" gap="0.25rem">
					<ReactCountryFlag
						countryCode={code}
						svg
						style={{ width: '1em', height: '1em' }}
						title={code}
					/>
					<Text c="dark.9" fz="md">
						{getCountryNameByCode(code) || code}
					</Text>
				</Flex>
			);
			const [first, ...rest] = citizenships;
			return (
				<CollapsedListCount
					count={rest.length}
					allowTooltipSummary={true}
					tooltipLabel={
						<Flex direction="column" gap="0.5rem" p="0.25rem">
							{rest.map((code, index) => (
								<React.Fragment key={`${code}-${index}`}>
									{renderCitizenship(code)}
								</React.Fragment>
							))}
						</Flex>
					}
				>
					{renderCitizenship(first)}
				</CollapsedListCount>
			);
		},
	}),
	createTextColumn({
		id: 'employeeIds',
		headerText: tMipManage('investors.table.header.employee_ids'),
		columnLabel: tMipManage('investors.table.header.employee_ids'),
		size: 220,
		minSize: 220,
		enableSorting: true,
		showFilter: true,
		filterVariant: 'text',
		filterText: tMipManage('investors.table.header.employee_ids'),
		cell: ({ row }) => {
			const employeeIds = row.original.employeeIds ?? [];
			if (employeeIds.length === 0) {
				return LONG_DASH;
			}
			const [first, ...rest] = employeeIds;
			return (
				<CollapsedListCount
					count={rest.length}
					allowTooltipSummary={true}
					tooltipLabel={
						<Flex direction="column" gap="0.5rem" p="0.25rem">
							{rest.map((id, index) => (
								<Text key={`${id}-${index}`} c="dark.9" fz="md">
									{id}
								</Text>
							))}
						</Flex>
					}
				>
					<Text
						fw={400}
						truncate
						lineClamp={1}
						style={{ flex: 1, wordBreak: 'break-all' }}
					>
						{first}
					</Text>
				</CollapsedListCount>
			);
		},
	}),
	...enabledCustomDetails.map((cd): ExtendedColumnDef<GetParsedInvestorRes> => {
		const cell = ({ row }: { row: { original: GetParsedInvestorRes } }) => {
			const value = row.original.customDetails.find((d) => d.customDetailId === cd.id)?.value;
			if (cd.dataType === CustomDetailDataTypeEnum.BOOLEAN) {
				return getBooleanLabel(value, tCommonUi);
			}
			return value || LONG_DASH;
		};

		if (cd.dataType === CustomDetailDataTypeEnum.BOOLEAN) {
			return {
				id: `customDetail:${cd.dataType}:${cd.id}`,
				header: () => <Text fw={600}>{cd.name}</Text>,
				columnLabel: cd.name,
				size: 200,
				minSize: 160,
				enableSorting: false,
				showFilter: true,
				filterValueType: 'boolean',
				filter: ({ column }) => <BooleanToggleFilter column={column} />,
				cell,
			};
		}

		// STRING / NUMBER → case-insensitive text filter (numeric range deferred).
		return createTextColumn<GetParsedInvestorRes>({
			id: `customDetail:${cd.dataType}:${cd.id}`,
			headerText: cd.name,
			columnLabel: cd.name,
			size: 200,
			minSize: 160,
			enableSorting: false,
			showFilter: true,
			filterVariant: 'text',
			filterText: cd.name,
			cell,
		});
	}),
	{
		id: 'actions',
		header: '',
		enableGlobalFilter: false,
		enableSorting: false,
		enableHiding: false,
		showFilter: false,
		minSize: 80,
		size: 80,
		cell: ({ row }) => (
			<InvestorActionCell
				investor={row.original}
				callbacks={actionCallbacks}
				labels={{
					sendInvite: tMipManage('investors.actions.send_invite'),
					sendDocument: tMipManage('investors.actions.send_document'),
					startAllocation: tMipManage('investors.actions.add_allocations'),
					startLeaver: tMipManage('investors.actions.add_leavers'),
					download: tMipManage('investors.actions.download'),
					removeInvestor: tMipManage(
						'investors.expanded_row.manage_investor.remove_investor_button'
					),
					eligibilityUnavailable: tMipManage('investors.actions.eligibility_unavailable'),
					requestKyc: tMipManage('investors.actions.request_kyc'),
				}}
				isLocked={isLocked}
				disableSendInvestorInvitation={disableSendInvestorInvitation}
				hasEditorRole={hasEditorRole}
				hasKycAdminRole={hasKycAdminRole}
				hasExportRole={hasExportRole}
			/>
		),
	},
];
